import React, { useContext, useEffect, useState } from 'react';

import {
    DeactivateLaunchDateInput,
    DeactivateLaunchDateOutput,
    GetServiceLaunchDateInput,
    GetServiceLaunchDateOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColumnState, ICellRendererParams } from 'ag-grid-community';
import { Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { AddLaunchDate } from 'src/components/AddServiceLaunch';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { ButtonGroupForGrid } from 'src/components/ButtonGroupForGrid';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { FilterInput } from 'src/components/FilterInput';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { EXCEL_DATE_STYLE_ID } from 'src/constants/agGrid';
import { VIEWER_ROLE_SET, WRITER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { ILaunchDatesRowData } from 'src/types/ILaunchDates';
import { IToast } from 'src/types/IToaster';
import { isOnGammaProd } from 'src/util/config';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getYYYYMMDDUTCStrFromTimestamp } from 'src/util/grid/cellGetter';
import { getPhoneToolLinkCellRenderer, getTerritoryFlagRenderer } from 'src/util/grid/cellRenderer';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import './ServiceLaunches.scss';

export const ServiceLaunches = () => {
    const { path } = useRouteMatch();
    const history = useHistory();
    const { setTopAlertMsg, userRole, setToasts, userInfo } = useContext(AppContext);

    const [launchDateRowData, setLaunchDateRowData] = useState<ILaunchDatesRowData[]>([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [isDeleteLaunchDateModalOpen, setIsDeleteLaunchDateModalOpen] = React.useState(false);
    const [launchDateToDelete, setLaunchDateToDelete] = useState<number | null>(null);
    const [launchDateToEdit, setLaunchDateToEdit] = useState<number | null>(null);

    useEffect(() => {
        if (!isDataLoaded) {
            contractConfigurationClient.getServiceLaunchDate(GetServiceLaunchDateInput.clone({})).subscribe(
                (response: GetServiceLaunchDateOutput) => {
                    setIsDataLoaded(true);
                    setLaunchDateRowData(response.serviceLaunches || []);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getServiceLaunches unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
        }
    }, [isDataLoaded]);

    const launchDateColDefs = [
        {
            field: 'serviceName',
            headerName: 'Service',
            filter: 'agSetColumnFilter',
            minWidth: 120
        },
        {
            field: 'territoryCode',
            headerName: 'Territory',
            filter: 'agSetColumnFilter',
            cellRenderer: (params: ICellRendererParams) => getTerritoryFlagRenderer(params, true, true),
            minWidth: 120
        },
        {
            field: 'launchDate',
            headerName: 'Launch Date (UTC)',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueFormatter: emptyCellFormatter,
            valueGetter: getYYYYMMDDUTCStrFromTimestamp,
            minWidth: 180
        },
        {
            field: 'endDate',
            headerName: 'End Date (UTC)',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'lessThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueFormatter: emptyCellFormatter,
            valueGetter: getYYYYMMDDUTCStrFromTimestamp,
            minWidth: 180
        },
        {
            field: 'creationDate',
            headerName: 'Created on (UTC)',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueGetter: getYYYYMMDDUTCStrFromTimestamp,
            minWidth: 180
        },
        {
            field: 'createdBy',
            headerName: 'Created by',
            comparator: dateComparator,
            filter: 'agSetColumnFilter',
            cellRenderer: getPhoneToolLinkCellRenderer,
            minWidth: 150
        },
        {
            field: 'action',
            headerName: 'Actions',
            filter: false,
            cellRenderer: (params: any) => {
                return (
                    <Column height="100%" alignmentVertical="center">
                        <Row spacing="200">
                            <Button
                                aria-label="edit"
                                type="link"
                                size="small"
                                href={`${ROUTES.SERVICE_LAUNCHES.INDEX}${ROUTES.SERVICE_LAUNCHES.ADD}${ROUTES.SERVICE_LAUNCHES.STEP.INPUT}`}
                                onClick={(href) => {
                                    history.push(href);
                                    setLaunchDateToEdit(params.data.launchDateID);
                                }}
                                disabled={!WRITER_ROLE_SET.has(userRole) || isOnGammaProd()}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                            </Button>
                            {
                                <Button
                                    aria-label="delete"
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        handleLaunchDateDeleteBtnClick(params.data.launchDateID);
                                    }}
                                    disabled={!WRITER_ROLE_SET.has(userRole) || isOnGammaProd()}
                                >
                                    <Icon className="button-icon" tokens={deleteTokens} />
                                </Button>
                            }
                        </Row>
                    </Column>
                );
            },
            minWidth: 120,
            maxWidth: 120
        }
    ];

    const launchDateColSortModel: ColumnState[] = [
        { colId: 'creationDate', sort: 'desc', sortIndex: 0 },
        { colId: 'launchDate', sort: 'desc', sortIndex: 1 }
    ];

    const handleLaunchDateDeleteBtnClick = (launchDateID: number) => {
        setLaunchDateToDelete(launchDateID);
        setIsDeleteLaunchDateModalOpen(true);
    };

    const handleSubmitWithToast = () => {
        if (launchDateToDelete === null) return;

        contractConfigurationClient
            .deactivateLaunchDate(
                DeactivateLaunchDateInput.clone({
                    changedBy: userInfo.username,
                    launchDateID: launchDateToDelete as number
                })
            )
            .subscribe(
                (response: DeactivateLaunchDateOutput) => {
                    setIsDataLoaded(false);
                    const toastMessage = 'Launch date deleted';
                    const toast: IToast = {
                        id: 'delete-launch-date-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                        message: toastMessage
                    };

                    setToasts([toast]);
                    setIsDeleteLaunchDateModalOpen(false);
                    setLaunchDateToDelete(null);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('DeactivateLaunchDate Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                    setLaunchDateToDelete(null);
                }
            );
    };

    const launchDateContent = (
        <Column className="service-launches-div" spacing="300" width="100%" height="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <ButtonGroupForGrid showAddLaunch={true} />
                </Row>
            </Box>
            <div className="service-launches-grid">
                <BaseAgGrid
                    columnDefs={launchDateColDefs}
                    sortModel={launchDateColSortModel}
                    rowData={launchDateRowData}
                    filterText={filterText}
                    loading={!isDataLoaded}
                />
            </div>
            <ConfirmationModal
                isOpen={isDeleteLaunchDateModalOpen}
                onClose={() => setIsDeleteLaunchDateModalOpen(false)}
                title="Confirm delete launch date"
                body="Are you sure you want to delete draft launch date?"
                showComments={false}
                primaryButtonText="Yes, delete launch date"
                primaryButtonOnClick={handleSubmitWithToast}
                secondaryButtonText="No, do not delete"
                secondaryButtonOnClick={() => setIsDeleteLaunchDateModalOpen(false)}
            />
        </Column>
    );

    return (
        <div className="service-launches-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {launchDateContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={WRITER_ROLE_SET} path={`${path}${ROUTES.SERVICE_LAUNCHES.ADD}`}>
                    <AddLaunchDate
                        setIsDataLoaded={setIsDataLoaded}
                        existingLaunchID={launchDateToEdit?.toString()}
                        setLaunchDateToEdit={setLaunchDateToEdit}
                    />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
